import React, { useState } from 'react';
import { UserRole } from '../lib/api';
import { Hammer, Building2 } from 'lucide-react';

interface RoleSelectProps {
  onRoleSelect: (role: UserRole) => void;
}

export const RoleSelect: React.FC<RoleSelectProps> = ({ onRoleSelect }) => {
  const [selected, setSelected] = useState<UserRole | null>(null);

  const handleSelect = (role: UserRole) => {
    setSelected(role);
    setTimeout(() => onRoleSelect(role), 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-[linear-gradient(30deg,transparent_0%,rgba(251,146,60,0.15)_50%,transparent_100%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(150deg,transparent_0%,rgba(253,186,116,0.12)_50%,transparent_100%)]"></div>
      </div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-4xl relative z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-3">Welcome to VendorBoom</h1>
          <p className="text-lg text-slate-300">Connect, verify, and grow your trade business</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={() => handleSelect('contractor')}
            className={`group relative bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-left border-2 ${
              selected === 'contractor' ? 'border-blue-500 scale-105 shadow-blue-500/20' : 'border-white/20 hover:border-blue-300'
            }`}
          >
            <div className="mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 group-hover:from-blue-600 group-hover:to-blue-700 flex items-center justify-center transition-all shadow-md group-hover:shadow-lg group-hover:scale-110">
                <Hammer className="w-7 h-7 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">I'm a Contractor</h2>
            <p className="text-slate-600 mb-6">Get verified, find jobs, and manage your credentials</p>
            <ul className="space-y-3 text-sm text-slate-600">
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mr-3 flex-shrink-0"></span>
                Browse and apply for jobs
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mr-3 flex-shrink-0"></span>
                Get verified and build trust
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mr-3 flex-shrink-0"></span>
                Manage insurance and licenses
              </li>
            </ul>
          </button>

          <button
            onClick={() => handleSelect('gc')}
            className={`group relative bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-left border-2 ${
              selected === 'gc' ? 'border-amber-500 scale-105 shadow-amber-500/20' : 'border-white/20 hover:border-amber-300'
            }`}
          >
            <div className="mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 group-hover:from-amber-600 group-hover:to-amber-700 flex items-center justify-center transition-all shadow-md group-hover:shadow-lg group-hover:scale-110">
                <Building2 className="w-7 h-7 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">I'm Hiring</h2>
            <p className="text-slate-600 mb-6">Find verified trades and manage your projects</p>
            <ul className="space-y-3 text-sm text-slate-600">
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 mr-3 flex-shrink-0"></span>
                Post jobs and receive applications
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 mr-3 flex-shrink-0"></span>
                Access verified contractors
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 mr-3 flex-shrink-0"></span>
                Track compliance and docs
              </li>
            </ul>
          </button>
        </div>

        <p className="text-center text-sm text-slate-400 mt-8">
          You can switch roles anytime from your dashboard
        </p>
      </div>
    </div>
  );
};
